/**
 * htmlToPdf - Reliably converts a full HTML document string into a multi-page PDF.
 *
 * Strategy: parse the source HTML with DOMParser, copy <style> tags into a
 * scoped wrapper while rewriting `body`/`html` selectors to a wrapper class,
 * then capture the wrapper with html2canvas and slice the resulting canvas
 * across A4 pages using jsPDF directly. This bypasses html2pdf.js quirks
 * where the wrapper gets display:none / 0x0 at capture time.
 */
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DOMPurify from "dompurify";

const ROOT_CLASS = "__pdf_render_root__";

const rewriteSelectors = (cssText) => {
  if (!cssText) return "";
  return cssText
    .replace(/(^|[\s,{}>+~])body(\s*[,{\s])/g, `$1.${ROOT_CLASS}$2`)
    .replace(/(^|[\s,{}>+~])html(\s*[,{\s])/g, `$1.${ROOT_CLASS}$2`);
};

const buildWrapper = (htmlContent) => {
  const parser = new DOMParser();
  const parsed = parser.parseFromString(htmlContent, "text/html");

  const wrapper = document.createElement("div");
  // Use absolute (not fixed), positive z-index, explicit dimensions and display.
  // 794px ≈ 210mm at 96dpi (the width html2canvas will capture).
  wrapper.style.cssText = [
    "position:absolute",
    "left:-10000px",
    "top:0",
    "width:794px",
    "min-height:1123px", // ≈ 297mm
    "background:#ffffff",
    "display:block",
    "visibility:visible",
    "opacity:1",
    "overflow:visible",
    "z-index:0",
  ].join(";");

  parsed.querySelectorAll("style").forEach((styleNode) => {
    const styleEl = document.createElement("style");
    styleEl.textContent = rewriteSelectors(styleNode.textContent || "");
    wrapper.appendChild(styleEl);
  });

  const root = document.createElement("div");
  root.className = ROOT_CLASS;
  // Make sure the root is a normal block container
  root.style.cssText = "display:block;width:100%;box-sizing:border-box;";
  root.innerHTML = DOMPurify.sanitize(
    parsed.body ? parsed.body.innerHTML : htmlContent,
    { ADD_TAGS: ["style"], ADD_ATTR: ["class", "style"] }
  );
  wrapper.appendChild(root);

  return wrapper;
};

const waitForLayout = async () => {
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
  } catch (e) {
    console.warn("document.fonts.ready unavailable, continuing:", e);
  }
  await new Promise((r) => setTimeout(r, 200));
};

export const downloadHtmlAsPdf = async (htmlContent, filename) => {
  const { pdf, safeName } = await renderHtmlToPdfDoc(htmlContent, filename);
  pdf.save(`${safeName}.pdf`);
};

async function renderHtmlToPdfDoc(htmlContent, filename, { scale = 2, quality = 0.95 } = {}) {
  if (!htmlContent) throw new Error("No HTML content provided");

  const wrapper = buildWrapper(htmlContent);
  document.body.appendChild(wrapper);

  try {
    await waitForLayout();

    const canvas = await html2canvas(wrapper, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      allowTaint: true,
      windowWidth: 794,
      width: wrapper.offsetWidth,
      height: wrapper.offsetHeight,
    });

    const safeName = (filename || "document")
      .replace(/[^a-zA-Z0-9ăîâșțĂÎÂȘȚ\s\-\.]/g, "")
      .trim() || "document";

    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // Margini A4 — conținutul este încadrat în pagină, nu lipit de margini.
    const MARGIN_X = 8;   // mm stânga/dreapta
    const MARGIN_Y = 10;  // mm sus/jos
    const contentWmm = pageW - 2 * MARGIN_X;
    const contentHmm = pageH - 2 * MARGIN_Y;

    const pxPerMm = canvas.width / contentWmm;
    const pageSlicePx = Math.floor(contentHmm * pxPerMm);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    // Caută (de jos în sus) un rând complet alb unde putem rupe pagina fără
    // să tăiem text/tabele la jumătate. Dacă nu găsește, taie la limita exactă.
    const findSmartCut = (from, to) => {
      const h = to - from;
      if (h <= 0) return to;
      let block;
      try {
        block = ctx.getImageData(0, from, canvas.width, h).data;
      } catch {
        return to; // canvas tainted — fallback la tăiere fixă
      }
      const stride = canvas.width * 4;
      for (let row = h - 1; row >= 0; row--) {
        let white = true;
        const off = row * stride;
        for (let x = 0; x < canvas.width; x += 3) {
          const i = off + x * 4;
          if (block[i] < 246 || block[i + 1] < 246 || block[i + 2] < 246) {
            white = false;
            break;
          }
        }
        if (white) return from + row;
      }
      return to;
    };

    let y = 0;
    let pageIndex = 0;
    while (y < canvas.height) {
      let end = Math.min(y + pageSlicePx, canvas.height);
      if (end < canvas.height) {
        // căutăm un punct de tăiere "curat" în ultimele 35% din pagină
        const minEnd = y + Math.floor(pageSlicePx * 0.65);
        const smart = findSmartCut(minEnd, end);
        if (smart > minEnd) end = smart;
      }
      const sliceHpx = end - y;

      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = sliceHpx;
      const sctx = slice.getContext("2d");
      sctx.fillStyle = "#ffffff";
      sctx.fillRect(0, 0, slice.width, slice.height);
      sctx.drawImage(canvas, 0, y, canvas.width, sliceHpx, 0, 0, canvas.width, sliceHpx);

      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(
        slice.toDataURL("image/jpeg", quality),
        "JPEG",
        MARGIN_X,
        MARGIN_Y,
        contentWmm,
        sliceHpx / pxPerMm
      );

      y = end;
      pageIndex++;
    }

    return { pdf, safeName };
  } finally {
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
  }
}
